import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import { type IUpdateAccountNameRequest } from "@apis/accounts/types"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import { Input } from "@components/ui/input"
import { Button } from "@components/ui/button"
import {
  useAccount,
  useUpdateAccountNameMutation,
} from "@apis/accounts/useAccounts"
import { accountKeys } from "@apis/accounts/queryKeys"
import { Loader2 } from "lucide-react"
import { useDrawer } from "@contexts/DrawerContext"

type TAccountNameFormProps = {
  account_id: string
}

export const AccountNameForm = ({ account_id }: TAccountNameFormProps) => {
  const updateAccountNameMutation = useUpdateAccountNameMutation()
  const queryClient = useQueryClient()
  const { closeDrawer } = useDrawer()

  const getAccountQuery = useAccount(account_id)

  const form = useForm<IUpdateAccountNameRequest>({
    mode: "onTouched",
    defaultValues: {
      membership_profile_info: {
        first_name: getAccountQuery.data?.membership_profile?.first_name || "",
        last_name: getAccountQuery.data?.membership_profile?.last_name || "",
      },
    },
  })

  const handleSubmit = (data: IUpdateAccountNameRequest) => {
    updateAccountNameMutation.mutate(
      {
        account_id: account_id,
        request: data,
      },
      {
        onSuccess: () => {
          toast.success("Successfully updated account name!")
          queryClient.invalidateQueries({
            queryKey: accountKeys.detail(account_id),
          })
          closeDrawer()
        },
        onError: (error) =>
          toast.error(`Error updating account name! ${error}`),
      },
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="tw:space-y-6">
        <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-2">
          <FormField
            control={form.control}
            name="membership_profile_info.first_name"
            rules={{ required: "First name is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter first name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="membership_profile_info.last_name"
            rules={{ required: "Last name is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter last name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="tw:flex tw:justify-end">
          <Button
            type="submit"
            disabled={
              updateAccountNameMutation.isPending ||
              !form.formState.isDirty ||
              !form.formState.isValid
            }
          >
            {updateAccountNameMutation.isPending ? (
              <>
                <Loader2 className="tw:animate-spin" />
                <span>Updating...</span>
              </>
            ) : (
              <span>Update Name</span>
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}
